import * as yup from 'yup';
import {
  combineRegex,
  isOldEnough,
  isYoungEnough,
  daysInMonth,
} from '@americanexpress/axp-field-validations-common';
import { alphaLower, alphaUpper, numbers, apostrophe, forwardSlash, hyphen, space, specialAlpha } from '../regular-expressions';
import { countryNameCodes } from '../utils';
import { allowedPlasticColorsForSAS } from '../primarySasSchema';

export const emailRegex = /^[\w!#$%&'*+./=?^`{|}~-]+@[\w.-]+\.[\da-z]+$/i;

export const employmentStatusValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
  'RETIRED',
  'STUDENT',
  'HOME_DUTIES',
  'UNEMPLOYED',
  'INDEPENDENT_INCOME',
];

export const industryDependentValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
];

// NOTE: used for backend validations
export const occupationDependentValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
];

// NOTE: UI has two fields that are combined into one on submit.
// Two validation sets are maintained.
export const STREET_MAXLENGTH = 40;
export const STREET_CHARSET = [numbers, alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen];
export const HOUSE_NUMBER_MAXLENGTH = 5;
export const HOUSE_NUMBER_CHARSET = [numbers, alphaLower, alphaUpper, forwardSlash, space, hyphen];
export const STREET_AND_HOUSE_NUMBER_LENGTH = STREET_MAXLENGTH + HOUSE_NUMBER_MAXLENGTH + 1;

export const plasticColor = yup.string()
  .required('supplementaryApplicants.plasticColor.required.error')
  .oneOf(allowedPlasticColorsForSAS, 'supplementaryApplicants.plasticColor.oneOf.error');

export const prefix = yup.string()
  .required('prefix.error')
  .oneOf(['MS', 'MR'], 'prefix.error');


export const title = yup.string()
  .notRequired()
  .oneOf(['', 'DOCTOR', 'PROFESSOR', 'PROFESSOR_DOCTOR'], 'title.error');

export const firstNameSchema = yup.string()
.required(`firstName.required.error`)
.min(2, `firstName.min.error`)
.max(20, `firstName.max.error`)
.matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `firstName.regex.error`);

export const middleNameSchema = yup.string()
.transform((value, originalValue) => (originalValue === '' ? undefined : value))
.notRequired()
.min(2, `middleName.min.error`)
.max(20, `middleName.max.error`)
.matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `middleName.regex.error`);

export const lastNameSchema = yup.string()
.required(`lastName.required.error`)
.min(2, `lastName.min.error`)
.max(20, `lastName.max.error`)
.matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `lastName.regex.error`);

export const nameOnCard = yup.string()
    .required(`nameOnCard.required.error`)
    .max(20, `nameOnCard.max.error`);

export const dateOfBirth = yup
.string()
.required(`dateOfBirth.required.error`)
.test('is-valid-length', `dateOfBirth.length.error`, (value) => (value.replace(/\./g, '').length === 8))
.matches(/^\d{2}\.\d{2}\.\d{4}/, `dateOfBirth.format.error`)
.test('is-month-valid', `dateOfBirth.isValidMonth.error`, (values) => {
  // eslint-disable-next-line no-unused-vars
  const [day, month, year] = values.split('.');
  return Number.parseInt(month, 10) < 13 && Number.parseInt(month, 10) > 0;
})
.test('is-date', `dateOfBirth.date.error`, (value) => {
  const [day, month, year] = value.split('.');
  if (month === '02') {
    return day <= daysInMonth['02'](year);
  }
  return day <= daysInMonth[month];
})
.test('is-old-enough', `dateOfBirth.isOldEnough.error`, (value) => {
  const [day, month, year] = value.split('.');
  const transformedValue = `${year}-${month}-${day}`;

  return isOldEnough(transformedValue, 18, 'Europe/Berlin');
})
.test('is-young-enough', `dateOfBirth.isYoungEnough.error`, (value) => {
  const [day, month, year] = value.split('.');
  const transformedValue = `${year}-${month}-${day}`;

  return isYoungEnough(transformedValue, 125, 'Europe/Berlin');
});

export const emailSchema = yup.string()
  .required(`email.required.error`)
  .min(5, `email.min.error`)
  .max(150, `email.max.error`)
  .email(`email.isEmail.error`)
  .test('email-additional-check', `email.isEmail.error`, (value) => emailRegex.test(value));

export const placeOfBirth = yup.string()
  .required('placeOfBirth.required.error')
  .min(1, 'placeOfBirth.min.error')
  .max(30, 'placeOfBirth.max.error')
  .matches(combineRegex([alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen]), 'placeOfBirth.regex.error');

export const nationality = yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .required('nationality.required.error')
  .oneOf(Object.values(countryNameCodes), 'nationality.enum.error');


export const mobilePhone =  yup.string()
  .required(`mobilePhoneNumber.required.error`)
  .min(5, `mobilePhoneNumber.min.error`)
  .max(15, `mobilePhoneNumber.max.error`)
  .matches(/^\d+$/, `mobilePhoneNumber.regex.error`)
  .matches(/^(?!0*$)[0-9]{5,15}$/, `mobilePhoneNumber.allzeros.error`);

export const hasSameAddress = yup.boolean().required();

const supplementaryStreetConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createStreetSchema();
    } else {
      return yup.string().notRequired();
    }
  });
};

const supplementaryHouseNumberConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createHouseNumberSchema();
    } else {
      return yup.string().notRequired();
    }
  });
};


const supplementaryPostalCodeConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createPostalCodeSchema();
    } else {
      return yup.string().notRequired();
    }
  });
};

const supplementaryCityConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createCitySchema();
    } else {
      return yup.string().notRequired();
    }
  });
};

const createStreetSchema = () => {
  return yup.string()
  .required(`street.required.error`)
  .min(1, `street.min.error`)
  .max(STREET_MAXLENGTH, `street.max.error`)
  .matches(combineRegex(STREET_CHARSET), `street.regex.error`);
};


const createHouseNumberSchema = () => {
  return yup.string()
  .required(`houseNumber.required.error`)
  .min(1, `houseNumber.min.error`)
  .max(HOUSE_NUMBER_MAXLENGTH, `houseNumber.max.error`)
  .matches(combineRegex(HOUSE_NUMBER_CHARSET), `houseNumber.regex.error`)
};

const createPostalCodeSchema = () => {
  return yup.string()
  .required(`postalCode.required.error`)
  .min(5, `postalCode.min.error`)
  .max(5, `postalCode.max.error`)
  .matches(combineRegex([numbers]), `postalCode.regex.error`)
};

const createCitySchema = () => {
  return yup.string()
  .required(`city.required.error`)
  .min(1, `city.min.error`)
  .max(30, `city.max.error`)
  .matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `city.regex.error`)
};

export const streetname = supplementaryStreetConditional();
export const houseNumber= supplementaryHouseNumberConditional();
export const postalCode= supplementaryPostalCodeConditional();
export const city= supplementaryCityConditional();

export const profession = yup.string()
  .required('employmentStatus.required.error')
  .oneOf(employmentStatusValues, 'employmentStatus.enum.error');


export const industryConditionals = {
  required: yup.string().required('industry.required.error'),
  notRequired: yup.string().notRequired(),
};

export const industry = yup.string()
  .when('profession', ([profession]) => {
    if (industryDependentValues.includes(profession)) {
      return industryConditionals.required;
    }
    return industryConditionals.notRequired;
  });


export const occupationConditionals = {
  required: yup.string().required('occupation.required.error'),
  notRequired: yup.string().notRequired(),
};

export const occupation = yup.string()
  .when('industry', ([industry]) => {
    if (industry) {
      return occupationConditionals.required;
    }

    return occupationConditionals.notRequired;
  });
